// Pallet desk shapes and the small pure rules the desk's screens share
// (pages/pallet-dispatch.tsx re-exports them, so tests and callers keep one
// import path).

// QP / HP / LP / MQP / FP are Palletways billing units. "OS" is Portal's
// OVERSIZED pallet (Alex, 2026-10-29): booked as a double — one lift
// billed as two LP, one label, and the consignment goes without a tail
// lift. The option label spells that out wherever the type is chosen.
export const PALLETWAYS_TYPES = ["QP", "HP", "LP", "OS", "MQP", "FP"] as const;
export const OVERSIZED_TYPE = "OS";

// The dropdown text for a Palletways type is the bare code; the oversized
// one is explained by palletwaysTypeTitle on the option's hover (Alex,
// 2026-10-29: the spelt-out text made every type dropdown far too wide).
// `t` is the i18n lookup.
export const palletwaysTypeLabel = (code: string, _t: (key: string) => string): string => code;
export const palletwaysTypeTitle = (code: string, t: (key: string) => string): string | undefined => (
    code === OVERSIZED_TYPE ? t("palletways_type_oversized") : undefined
);

export interface DeskPallet {
    sku: string;
    title: string | null;
    size_name: string | null;
    type: string | null;
    weight_kg: number;
    weight_defaulted: boolean;
    /** Pallet height (own, or its size's default); null when not recorded. */
    height_cm?: number | null;
    /** Volumetric weight under the warehouse's formula; null when off or no height. */
    volumetric_kg?: number | null;
}
export interface DeskBooking {
    id: number;
    status: string;
    /** ready | booking | booked | released | shipped | failed | cancelled */
    state: string;
    con_no: string | null;
    service_name: string | null;
    tracking_id: string | null;
    barcodes: string[];
    error: string | null;
    /** Direct carrier (JAS): the carrier's shipment id and waybill, and whether the documents are stored. */
    shipment_id?: string | null;
    waybill_no?: string | null;
    has_label?: boolean;
    has_waybill?: boolean;
    /** Raben: the shipment reference (= the order ref). */
    reference?: string | null;
    /** Raben: the delivery window Raben planned (YYYY-MM-DD). */
    planned_delivery?: { from: string; to: string } | null;
    /** Raben: whether the eWZ document upload went through (non-blocking). */
    wz?: { uploaded: boolean; error: string | null } | null;
}
/** A pallet carrier the warehouse ships on; `mode` says how the desk works it:
 *  api    — book → label → "Release today's pallets" (Palletways)
 *  direct — one Ship books at the carrier, prints its documents and ships (JAS, Raben)
 *  manual — the operator booked on the carrier's site; Ship records it (DB Schenker) */
export interface DeskCarrier {
    code: string;
    name: string;
    mode: "api" | "direct" | "manual";
    countries: string[];
    account_id?: number | null;
    /** Set when an API carrier is served as manual because its account is not ready
     *  (credentials or service missing, in the Settings card's words): the operator
     *  books on the carrier's own site and types the waybill until Settings are done. */
    fallback?: { from: "api" | "direct"; missing: string[] } | null;
}

/** Carriers whose day's shipments get a loading list (lista załadunkowa) the driver signs. */
export const LOADING_LIST_CARRIERS: readonly string[] = ["raben"];

/** Today's date as YYYY-MM-DD in Warsaw (the desk's warehouse), for the loading-list date. */
export const todayWarsaw = (): string => {
    try {
        const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Warsaw", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
        const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
        const iso = `${get("year")}-${get("month")}-${get("day")}`;
        if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
    } catch { /* fall through to the local date */ }
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
/** A PDF document that came back from a direct carrier (label, waybill). */
export interface DeskDocument {
    format: "PDF";
    contentBase64: string;
    pages?: number;
}
/** An attachment on the order — labels first, then invoices and the rest. */
export interface DeskAttachment {
    attachment_id: string;
    filename: string;
    mime_type: string;
    /** shipping_label | invoice | other */
    kind?: string;
    created_at: string;
}
export interface DeskDelivery {
    name: string | null; company: string | null; line_1: string | null; line_2: string | null;
    city: string | null; county: string | null; postcode: string | null; country?: string | null;
    phone: string | null; phone_country?: string | null; email: string | null;
}
export interface DeskOrder {
    order_id: string;
    order_number: string | null;
    /** The sales channel, for the order header's badge. */
    sales_channel?: string | null;
    delivery: DeskDelivery;
    pallets: DeskPallet[];
    total_kg: number;
    problems: Array<{ code: string; sku?: string; message: string }>;
    booking: DeskBooking | null;
    /** Chosen by the BE from the delivery country; null when no pallet carrier serves it. */
    carrier?: DeskCarrier | null;
    /** Every enabled pallet carrier serving the order's country; more than one = the operator chooses. */
    carrier_options?: DeskCarrier[];
    /** The pallet shipping rule that picked the carrier (carrier_routing "rules"); null when none matched. */
    carrier_rule?: { id: number; name: string; service_id?: number } | null;
    /** "rules" = the warehouse has pallet shipping rules; "country" = the carrier's country list decided. */
    carrier_routing?: "rules" | "country";
    attachments?: DeskAttachment[];
}
export interface DeskData {
    enabled: boolean;
    /** Palletways can be booked here (account + service row). */
    api_ready: boolean;
    carriers: DeskCarrier[];
    orders: DeskOrder[];
}

/** The desk's corrections to a pallet before booking: { [sku]: { type?, weight_kg? } }. */
export type Overrides = Record<string, { type?: string; weight_kg?: number }>;

/** The desk's state for an order: an order with a cancelled or failed
 *  booking is ready to be booked again. */
export type DeskState = "ready" | "booking" | "booked" | "released";
export const deskState = (order: DeskOrder): DeskState => {
    const s = order.booking?.state;
    if (s === "booked" || s === "released" || s === "booking") return s;
    return "ready";
};

/** The carriers the operator can choose between for this order (empty when none serves the country). */
export const carrierOptionsOf = (order: DeskOrder | null | undefined): DeskCarrier[] =>
    order?.carrier_options?.length ? order.carrier_options : order?.carrier ? [order.carrier] : [];

/** The rule's name when a pallet shipping rule chose this order's carrier, else null. */
export const routedRuleName = (order: DeskOrder | null | undefined): string | null =>
    order?.carrier_routing === "rules" && order.carrier_rule?.name ? order.carrier_rule.name : null;

/** The i18n key for "no carrier": rule-routed warehouses have no matching pallet rule, others no carrier for the country. */
export const noCarrierKeyOf = (order: DeskOrder | null | undefined): "pallet_dispatch_no_pallet_rule" | "pallet_dispatch_no_carrier_for_country" =>
    order?.carrier_routing === "rules" ? "pallet_dispatch_no_pallet_rule" : "pallet_dispatch_no_carrier_for_country";

/** The order with the chosen carrier swapped in (the screens read order.carrier). */
export const withChosenCarrier = (order: DeskOrder, code: string | null): DeskOrder => {
    if (!code || order.carrier?.code === code) return order;
    const chosen = carrierOptionsOf(order).find((c) => c.code === code);
    return chosen ? { ...order, carrier: chosen } : order;
};

export const isManualOrder = (order: DeskOrder | null | undefined): boolean => order?.carrier?.mode === "manual";
export const isDirectOrder = (order: DeskOrder | null | undefined): boolean => order?.carrier?.mode === "direct";
/** Ships from the confirm screen (manual or direct) rather than booking on the pack screen (api). */
export const isShipOrder = (order: DeskOrder | null | undefined): boolean => isManualOrder(order) || isDirectOrder(order);

/** The customer's phone with its dial code in front ("+48 501234567"): the
 *  number is stored without the code, and a carrier's site wants both. */
export const phoneDisplay = (delivery: DeskDelivery): string | null => {
    const number = (delivery.phone ?? "").trim();
    if (!number) return null;
    const dial = (delivery.phone_country ?? "").replace(/[^0-9+]/g, "");
    if (!dial) return number;
    return `${dial.startsWith("+") ? dial : `+${dial}`} ${number.replace(/^0+/, "")}`;
};

/** A pallet scan matches its SKU or its put-away tote barcode (<SKU>-T). */
export const palletOfScan = (order: DeskOrder, scan: string): DeskPallet | null => {
    const code = scan.trim().toUpperCase();
    const sku = code.endsWith("-T") ? code.slice(0, -2) : code;
    return order.pallets.find((p) => p.sku.toUpperCase() === code || p.sku.toUpperCase() === sku) ?? null;
};

const ATTACHMENT_KINDS = new Set(["shipping_label", "invoice", "other"]);
export const attachmentKind = (att: DeskAttachment): string => (att.kind && ATTACHMENT_KINDS.has(att.kind) ? att.kind : "other");

export const isPdfAttachment = (att: DeskAttachment): boolean =>
    att.mime_type === "application/pdf" || /\.pdf$/i.test(att.filename);

/** The address lines the screens print, in order, blanks dropped. */
export const addressLines = (d: DeskDelivery): string[] =>
    [d.name, d.company, d.line_1, d.line_2, d.city, d.county, d.postcode].filter((x): x is string => !!x);
